import { createApp, ref, onMounted, computed, watch, useTemplateRef } from 'vue'
import { createI18n } from 'vue-i18n'

class LtSelect extends HTMLElement {
  static get observedAttributes() {
    return ['value', 'disabled', 'name'];
  }

  constructor() {
    super();
    this._value = null;
    this._hiddenInput = null;
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: inline-block; font-size: 14px; }
        .container { position: relative; }
        .trigger {
          font-size: 12px;
          box-sizing: border-box;
          width: 100%;
          padding: 9px 31px 9px 7px;
          border: 1px solid rgba(32, 32, 37, 0.08);
          border-radius: 4px;
          cursor: pointer;
          background: #fff;
          line-height: 16px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: border-color 0.3s ease;
        }
        .trigger.has-value {
          border-color: #202025;
        }
        .container.open .trigger,
        .trigger:focus {
          outline: none;
          border-color: #202025;
        }
        .trigger.disabled { background: #f5f5f5; cursor: not-allowed; }
        .arrow {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          transition: transform 0.3s ease;
          pointer-events: none;
          max-height: 240px;
          overflow-y: auto;
          background: #fff;
        }

        .container.open .arrow {
          transform: translateY(-50%) rotate(180deg);
        }

        .panel {
          position: fixed;
          display: none;
          width: fit-content;
          margin: 0;
          padding: 0;
          border: 1px solid rgb(242, 242, 242);
          border-radius: 4px;
          max-height: 240px;
          overflow-y: auto;
          background: #fff;
          z-index: 9999;
        }

        .panel.is-open {
          display: block;
        }
  
        .t4s-current-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .t4s-current-scrollbar::-webkit-scrollbar-track {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .t4s-current-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.12);
        }

        .t4s-current-scrollbar.t4s-scheme-light::-webkit-scrollbar-track {
          background-color: rgba(255, 255, 255, 0.05);
        }

        .t4s-current-scrollbar.t4s-scheme-light::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.12);
        } 
      </style>
      <div class="container">
        <input type="hidden" name="items[][id]" autocomplete="off"/>
        <div class="trigger" part="trigger" id="trigger" tabindex="0">
          <slot name="empty"></slot>
        </div>
        <svg class="arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M13.6714 6.28613L12.7286 5.34332L8.01453 10.0574L3.30049 5.34332L2.35768 6.28613L7.54309 11.4715C7.54309 11.4715 7.54313 11.4716 8.01453 11.0002L7.54309 11.4715L8.01453 11.943L13.6714 6.28613Z" fill="#515058"/>
        </svg>
        <div class="panel t4s-current-scrollbar" part="panel" data-panel>
          <slot/>
        </div>
      </div>
    `;

    this._container = this.shadowRoot.querySelector('.container');
    this._trigger = this.shadowRoot.querySelector('.trigger');
    this._panel = this.shadowRoot.querySelector('.panel');
    this._hiddenInput = this.shadowRoot.querySelector('input[type="hidden"]');
    this._slot = this._panel.querySelector('slot');
    this._options = this._slot.assignedElements();

    this._onDocClick = this._onDocClick.bind(this);
    this._cleanupAutoUpdate = null;
  }

  connectedCallback() {

    this._trigger.addEventListener('click', () => this._toggle());
    this._bindOptions();
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    if (name === 'value') {
      this._value = newVal;
      this._syncSelected();
      this._syncInput();
    }
    if (name === 'disabled') {
      this._trigger.classList.toggle('disabled', this.disabled);
      this._syncInput();
    }
    if (name === 'name') {
      this._syncInput();
    }
  }

  get value() {
    return this._value;
  }
  set value(v) {
    this.setAttribute('value', v ?? '');
    this._fireChange();
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  _syncSelected() {
    const selectedOption = this._options.find(option => option.dataset.value === this._value);
    if (!selectedOption) return;
    this._options.forEach(option => {
      option.classList.toggle('selected', option.dataset.value === this._value);
    });

    this._trigger.textContent = selectedOption.textContent;
    // 有选中时加 class，无选中时移除
    if (this._value) {
      this._trigger.classList.add('has-value');
    } else {
      this._trigger.classList.remove('has-value');
    }
  }

  _handleSelect(val) {
    this.value = val;
    this._close();
    setTimeout(() => {
      this._trigger.focus();
    }, 0);
  }

  _toggle() {
    if (this.disabled) return;
    this._panel.classList.contains('is-open') ? this._close() : this._open();
  }

  _open() {
    try {
      this._panel.classList.add('is-open');
      this._container.classList.add('open');
      // allow CSS transitions/layout to settle
      this._positionPanel();

      this._cleanupAutoUpdate = window.FloatingUIDOM.autoUpdate(this._trigger, this._panel, this._positionPanel.bind(this));
    } catch (e) {
      console.error('Failed to open popover:', e);
    }
  }

  _close() {
    try {
      this._panel.classList.remove('is-open');
      this._container.classList.remove('open');
      if (this._cleanupAutoUpdate) {
        this._cleanupAutoUpdate();
        this._cleanupAutoUpdate = null;
      }
    } catch (e) {
      console.error('Failed to close popover:', e);
    }
  }

  _positionPanel() {
    const { computePosition, offset, flip } = window.FloatingUIDOM;
    computePosition(this._trigger, this._panel, {
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip(),
      ],
    }).then((data) => {
      Object.assign(this._panel.style, {
        left: `${data.x}px`,
        top: `${data.y}px`,
        minWidth: `${this._trigger.offsetWidth}px`,
      });
    });
  }

  _onDocClick(e) {
    if (!this.contains(e.target) && !this.shadowRoot.contains(e.target)) {
      this._close();
    }
  }

  _fireChange() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this._value },
      bubbles: true,
      composed: true
    }));
  }

  _syncInput() {
    if (!this._hiddenInput) return;
    this._hiddenInput.value = this._value || '';
    this._hiddenInput.name = this.getAttribute('name') || '';
    this._hiddenInput.disabled = this.disabled;
  }

  _bindOptions() {
    this._options.forEach(opt => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        this._handleSelect(opt.dataset.value, opt);
      });
    })
  }


}
if (!customElements.get('lt-select')) {
  customElements.define('lt-select', LtSelect);
}

class ProductGroupForm extends HTMLElement {
  // CSS 选择器常量
  static SELECTORS = {
    totalPrice: '[data-groups-total-price]',
    item: '[data-groups-pr-item]',
    select: '[data-groups-pr-sl]',
    checkbox: '[data-groups-pr-ck]',
    quantity: '[data-groups-qty-value]',
    itemPrice: '[data-groups-item-price]',
    itemInput: '[name*="items[]"]',
    image: '[data-groups-img]',
    submitBtn: '.t4s-product-form__submit',
  };

  // 事件名称常量
  static EVENTS = {
    change: 'change.groups'
  };

  // CSS 类名常量
  static CSS_CLASSES = {
    checked: 'is--checked',
    loading: "is--loading"
  };

  constructor() {
    super();
    // 存储每个商品的价格
    this.priceArray = [];
    // 存储每个商品的对比价格
    this.comparePriceArray = [];
  }

  /**
   * 组件挂载到 DOM 时调用
   */
  connectedCallback() {
    this.$mainMedia = $(document.querySelector(".lt-qv [data-main-media]"));
    // 查找表单元素（可以是组件本身或内部的 form）
    this.$form = this.querySelector('form') ? $(this.querySelector('form')) : $(this);
    this.$submitBtn = this.$form.find(ProductGroupForm.SELECTORS.submitBtn);
    this.$totalPrice = this.$form.find(ProductGroupForm.SELECTORS.totalPrice);

    // 初始化所有商品项的价格
    this.$form.find(ProductGroupForm.SELECTORS.item).each((index, element) => {
      this._updateItemPrice(element, index);
    });

    // 更新总价并绑定事件
    this._updateTotalPrice();
    this._bindEventListeners();

    this.$form.on(ProductGroupForm.EVENTS.change, (e) => {
      this.$form.find(ProductGroupForm.SELECTORS.item).each((index, element) => {
        this._updateItemPrice(element, index);
      });
      this._updateTotalPrice();
    })
  }

  /**
   * 组件从 DOM 移除时调用（清理事件监听器）
   */
  disconnectedCallback() {
    if (this.$form) {
      this.$form.off(ProductGroupForm.EVENTS.change);
    }
  }
  _switchSelectMedia(productId) {
    if (this.$mainMedia && this.$mainMedia.hasClass("flickityt4s-enabled")) {
      this.$mainMedia.trigger("update.flickityt4s")
      const visibleIndex = this.$mainMedia.find(`[data-pid="${productId}"]:visible`).index();
      this.$mainMedia.flickityt4s("selectCell", Math.max(visibleIndex, 0), false, false);
    }
  }

  /**
   * 绑定所有事件监听器
   */
  _bindEventListeners() {
    const { SELECTORS, EVENTS, CSS_CLASSES } = ProductGroupForm;

    // 监听商品规格选择变化
    this.$form.on(EVENTS.change, SELECTORS.select, (event) => {
      this._handleItemChange(event.currentTarget);
    });

    // 监听商品复选框变化
    this.$form.on(EVENTS.change, SELECTORS.checkbox, (event) => {
      const checkbox = event.currentTarget;
      const $item = $(checkbox).closest(SELECTORS.item);
      const itemIndex = $item.index();
      const $image = this.$form.find(`[data-groups-img="${itemIndex}"]`);
      const $input = $item.find(SELECTORS.itemInput);

      if (checkbox.checked) {
        // 选中商品
        $item.addClass(CSS_CLASSES.checked);
        this._updateItemPrice($item[0], itemIndex);
        $image.fadeIn(300);
        $input.prop('disabled', false);
      } else {
        // 取消选中商品
        $item.removeClass(CSS_CLASSES.checked);
        this.priceArray[itemIndex] = 0;
        this.comparePriceArray[itemIndex] = 0;
        $image.fadeOut(300);
        $input.prop('disabled', true);
      }

      this._updateTotalPrice();
    });

    // 监听商品数量变化
    this.$form.on(EVENTS.change, SELECTORS.quantity, (event) => {
      const $item = $(event.currentTarget).closest(SELECTORS.item);
      const itemIndex = $item.index();
      this._updateItemPrice($item[0], itemIndex);
      this._updateTotalPrice();
    });

    const $body = $('body')
    this.$form.on('submit', (e) => {
      e.preventDefault();

      if (this.$submitBtn.attr("aria-disabled")) {
        return;
      }

      try {
        this.$submitBtn.addClass(ProductGroupForm.CSS_CLASSES.loading).attr("aria-disabled", true);
        this.$submitBtn.find(".loading-overlay__spinner").removeAttr("hidden");
        this.$submitBtn.find(".t4s-svg-spinner").removeAttr("hidden");

        const items = this._getItems()
        $body.trigger('lt:product:addItem', {
          bid: this.$submitBtn.data('bid') || '',
          isEdit: window.ltQv.isEdit || false,
          items
        })

        $body.trigger("modalt4s:closed")
      } finally {
        this.$submitBtn.removeClass(ProductGroupForm.CSS_CLASSES.loading).removeAttr("aria-disabled");
        this.$submitBtn.find(".loading-overlay__spinner").attr("hidden", true);
        this.$submitBtn.find(".t4s-svg-spinner").attr("hidden", true);
      }
    })
  }

  _getItems() {
    const pItem = this.$form.find(ProductGroupForm.SELECTORS.item);

    const items = [];
    pItem.each(function () {
      const $item = $(this);
      const $select = $item.find(ProductGroupForm.SELECTORS.select);
      const $selectOption = $select.find('.option.selected');
      if (!$selectOption.length) return;

      const quantity = parseInt($item.find(ProductGroupForm.SELECTORS.quantity).val()) || 0;
      items.push({
        id: $item.data('pid'),
        vid: $selectOption.data('value'),
        title: $item.data('title'),
        url: $item.data('url'),
        max: $selectOption.data('max'),
        price: $selectOption.data('price'),
        cpprice: $selectOption.data('cpprice'),
        image: $selectOption.data('image'),
        options: JSON.parse($selectOption.attr('data-options') || '{}'),
        quantity,
        is_selected: true,
        priceVaries: false,
        isEdit: $item.attr('data-edit') === 'true' ? true : false
      })
    })

    return items
  }

  /**
   * 更新单个商品的价格
   * @param {Element} itemElement - 商品元素
   * @param {number} index - 商品索引
   */
  _updateItemPrice(itemElement, index) {
    const { SELECTORS } = ProductGroupForm;
    const $item = $(itemElement);
    const $select = $item.find(SELECTORS.select);

    // 如果用户没有选择规格，不计算价格
    const selectValue = $select.attr('value') || $select.val();
    if (!selectValue) {
      this.priceArray[index] = 0;
      this.comparePriceArray[index] = 0;
      return;
    }

    const quantity = parseInt($item.find(SELECTORS.quantity).val()) || 0;
    const unitPrice = parseInt($select.attr('data-price')) || 0;
    const unitComparePrice = parseInt($select.attr('data-cpprice')) || 0;

    // 计算总价
    const totalPrice = unitPrice * quantity;
    const totalComparePrice = unitComparePrice * quantity;

    // 保存到数组
    this.priceArray[index] = totalPrice;
    this.comparePriceArray[index] = Math.max(totalPrice, totalComparePrice);
  }

  /**
   * 处理商品选项变化（规格选择）
   * @param {Element} selectElement - 选择框元素
   */
  _handleItemChange(selectElement) {
    const { SELECTORS } = ProductGroupForm;
    const $select = $(selectElement);
    const $option = $select.find('.option.selected');
    const $item = $select.closest(SELECTORS.item);
    const customIndex = $item.data('index');
    const itemIndex = customIndex !== undefined ? customIndex : $item.index();

    window.luteUtil.rIC(() => {
      const pid = $item.data('pid')
      this._switchSelectMedia(pid);
    })

    // 获取选中选项的数据
    const imageUrl = $option.data('img');
    const maxQuantity = $option.data('max');
    const unitPrice = parseInt($option.data('price')) || 0;
    const unitComparePrice = parseInt($option.data('cpprice')) || 0;

    // 更新数量输入框
    const $quantityInput = $item.find(SELECTORS.quantity);
    const quantity = parseInt($quantityInput.val()) || 0;
    $quantityInput.attr('max', maxQuantity);

    // 更新图片（懒加载）
    const $image = this.$form.find(`[data-groups-img="${itemIndex}"] img`);
    if ($image.attr('data-orginal') !== imageUrl) {
      $image.attr({
        'data-src': imageUrl,
        'data-orginal': imageUrl
      }).removeClass('lazyloadt4sed').addClass('lazyloadt4s');
    }

    // 计算价格
    const comparePrice = Math.max(unitPrice, unitComparePrice);
    const displayPrice = unitPrice
    const displayComparePrice = comparePrice
    const totalPrice = unitPrice * quantity;
    const totalComparePrice = unitComparePrice * quantity;

    // 更新价格显示
    const $priceElement = $item.find(SELECTORS.itemPrice);
    this._renderPrice($priceElement, displayPrice, displayComparePrice);

    // 更新选择框的价格属性
    $select.attr({
      'data-price': displayPrice,
      'data-cpprice': displayComparePrice
    });

    // 更新数组并刷新总价
    if (this.priceArray[itemIndex] !== totalPrice ||
      this.comparePriceArray[itemIndex] !== totalComparePrice) {
      this.priceArray[itemIndex] = totalPrice;
      this.comparePriceArray[itemIndex] = totalComparePrice;
      this._updateTotalPrice();
    }
  }

  /**
   * 渲染价格 HTML
   * @param {jQuery} $element - 价格显示元素
   * @param {number} price - 当前价格
   * @param {number} comparePrice - 对比价格
   */
  _renderPrice($element, price, comparePrice) {
    const formattedPrice = T4SThemeSP.Currency.formatMoney(price);
    const formattedComparePrice = T4SThemeSP.Currency.formatMoney(comparePrice);

    if (comparePrice > price) {
      $element.html(`<ins>${formattedPrice}</ins><del>${formattedComparePrice}</del>`);
    } else {
      // 无对比价格，只显示价格
      $element.html(`<ins>${formattedPrice}</ins>`);
    }
  }

  /**
   * 更新总价格
   */
  _updateTotalPrice() {
    // 计算总价
    const totalPrice = this.priceArray.reduce((sum, price) => sum + price, 0);
    const totalComparePrice = this.comparePriceArray.reduce((sum, price) => sum + price, 0);

    // 渲染总价格
    this._renderPrice(this.$totalPrice, totalPrice, totalComparePrice);

    // 触发货币更新事件
    $(document).trigger('currency:update');
  }
}

// 注册自定义元素
if (!customElements.get('product-group-form')) {
  customElements.define('product-group-form', ProductGroupForm);
}

class QuantitySelector extends HTMLElement {
  static get observedAttributes() {
    return ['value', 'min', 'max', 'step', 'name', 'disabled'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          align-items: center;
          border-radius: 4px;
          overflow: visible;
          background: transparent;
          --height: 24px;
          font-size: 13px;
          background: rgba(32,32,37,0.08);
        }
        
        .collapsed {
          box-sizing: border-box;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 4px;
          height: var(--height);
          line-height: var(--height);
          cursor: pointer;
          user-select: none;
          font-weight: 500;
          min-width: 24px;
        }

        :host([disabled]) .collapsed{
          opacity: 0.5;
          cursor: not-allowed;
        }
        .controls {
          display: inline-flex;
          align-items: center;
          gap: 0;
          border-radius: 4px;
          overflow: hidden;
          height: var(--height);
          transform-origin: left center;
        }
        :host(:not([open])) .controls {
          display: none;
        }

        :host([open]) .collapsed {
          display: none;
        }

        .controls button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: var(--height);
          cursor: pointer;
          border: none;
          padding: 0;
          background: transparent;
          color: #202025;
        }
        .controls button:disabled { opacity: 0.3; cursor: not-allowed; }
        .controls button svg { width: 16px; height: 16px }
        .controls input {
          border: none;
          box-sizing: border-box;
          padding: 0;
          width: 30px;
          height: var(--height);
          border-inline: 1px solid #fff;
          text-align: center;
          font-size: 12px;
          font-weight: 500;
          background-color: transparent;
          -moz-appearance: textfield;
        }
        .controls input::-webkit-outer-spin-button,
        .controls input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
       .controls input:focus {
          outline: none;
        }
        @media (min-width: 640px) {
          :host {
            --height: 28px;
          }
          .collapsed {
            padding: 0 8px;
          }

          .controls input {
            width: 37px;
          }
          .controls button {
            width: 23px;
          }
        }
      </style>

      <div class="collapsed" part="collapsed" aria-hidden="false">
        <span class="label">X1</span>
      </div>

      <div class="controls" part="controls" aria-hidden="true">
        <button data-decrease-qty type="button" class="t4s-quantity-selector is--minus">
          <svg focusable="false" class="icon icon--minus" viewBox="0 0 16 16" role="presentation">
            <rect x="4" y="7.4" width="8" height="1.2" fill="currentColor"/>
          </svg>
        </button>
        <input
          type="number"
          class="t4s-quantity-input"
          size="4"
          pattern="[0-9]*"
          inputmode="numeric"
        >
        <button data-increase-qty type="button"  class="t4s-quantity-selector is--plus">
          <svg focusable="false" class="icon icon--plus" viewBox="0 0 16 16" role="presentation">
            <rect x="4" y="7.4" width="8" height="1.2" fill="currentColor"/>
            <rect x="8.6" y="4.0" width="8" height="1.2" transform="rotate(90 8.6 4.0)" fill="currentColor"/>
          </svg>
        </button>
      </div>
    `;

    this._collapsed = this.shadowRoot.querySelector('.collapsed');
    this._collapsedLabel = this.shadowRoot.querySelector('.collapsed .label');
    this._controls = this.shadowRoot.querySelector('.controls');
    this._input = this.shadowRoot.querySelector('.controls input');
    this._decreaseBtn = this.shadowRoot.querySelector('[data-decrease-qty]');
    this._increaseBtn = this.shadowRoot.querySelector('[data-increase-qty]');

    this._boundDocClick = this._onDocClickBound = null;
    this._isOpen = false;
  }

  connectedCallback() {
    this._decreaseBtn.addEventListener('click', this._decreaseBound = this._decrease.bind(this));
    this._increaseBtn.addEventListener('click', this._increaseBound = this._increase.bind(this));
    this._input.addEventListener('input', this._onInputBound = this._onInput.bind(this));
    this._input.addEventListener('change', this._onChangeBound = this._onChange.bind(this));
    this._collapsed.addEventListener('click', this._toggleBound = this._toggleCollapse.bind(this));

    // 初始化值
    const initial = this.getAttribute('value') || this.getAttribute('default') || '1';
    this._input.value = initial;
    this._updateCollapsedLabel();
    this._updateButtons();
  }

  disconnectedCallback() {
    this._decreaseBtn.removeEventListener('click', this._decreaseBound);
    this._increaseBtn.removeEventListener('click', this._increaseBound);
    this._input.removeEventListener('input', this._onInputBound);
    this._input.removeEventListener('change', this._onChangeBound);
    this._collapsed.removeEventListener('click', this._toggleBound);
    if (this._boundDocClick) document.removeEventListener('click', this._boundDocClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value':
        this._input.value = newValue;
        this._updateCollapsedLabel();
        this._updateButtons();
        break;
      case 'min':
        this._input.min = newValue;
        this._updateButtons();
        break;
      case 'max':
        this._input.max = newValue;
        this._updateButtons();
        break;
      case 'step':
        this._input.step = newValue;
        break;
      case 'name':
        this._input.name = newValue;
        break;
      case 'disabled':
        this._input.disabled = this.disabled;
        this._decreaseBtn.disabled = this.disabled;
        this._increaseBtn.disabled = this.disabled;
        break;
    }
  }

  get value() {
    return parseInt(this._input.value) || 0;
  }

  set value(val) {
    this.setAttribute('value', val);
    // ensure input + label updated
    if (this._input) this._input.value = val;
    this._updateCollapsedLabel();
  }

  get min() {
    return parseInt(this.getAttribute('min')) || 1;
  }

  set min(val) {
    this.setAttribute('min', val);
  }

  get max() {
    return parseInt(this.getAttribute('max')) || 999;
  }

  set max(val) {
    this.setAttribute('max', val);
  }

  get step() {
    return parseInt(this.getAttribute('step')) || 1;
  }

  set step(val) {
    this.setAttribute('step', val);
  }

  get name() {
    return this.getAttribute('name');
  }

  set name(val) {
    this.setAttribute('name', val);
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(val) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  _decrease() {
    const newValue = Math.max(this.min, this.value - this.step);
    this.value = newValue;
    this._fireChange();
  }

  _increase() {
    const newValue = Math.min(this.max, this.value + this.step);
    this.value = newValue;
    this._fireChange();
  }

  _toggleCollapse() {
    if (this.disabled) return;
    if (this._isOpen) {
      this._collapseControls();
    } else {
      this._expandControls();
    }
  }

  _expandControls() {
    this.setAttribute('open', '');
    this._controls.setAttribute('aria-hidden', 'false');
    this._collapsed.setAttribute('aria-hidden', 'true');
    this._isOpen = true;
    // add doc click to close when clicking outside
    this._boundDocClick = (e) => {
      const path = e.composedPath ? e.composedPath() : (e.path || []);
      if (!path.includes(this) && !path.includes(this.shadowRoot)) {
        this._collapseControls();
      }
    };
    document.addEventListener('click', this._boundDocClick);
    // focus input
    requestAnimationFrame(() => this._input.focus());
  }

  _collapseControls() {
    this.removeAttribute('open');
    this._controls.setAttribute('aria-hidden', 'true');
    this._collapsed.setAttribute('aria-hidden', 'false');
    this._isOpen = false;
    if (this._boundDocClick) {
      document.removeEventListener('click', this._boundDocClick);
      this._boundDocClick = null;
    }
    this._updateCollapsedLabel();
  }

  _onInput() {
    // 校验输入并限制到 min/max
    let raw = parseInt(this._input.value);
    if (Number.isNaN(raw)) raw = this.min;
    if (raw > this.max) raw = this.max;
    if (raw < this.min) raw = this.min;
    // 如果输入值被修正，更新输入框显示
    if (String(this._input.value) !== String(raw)) {
      this._input.value = raw;
    }
    // 同步属性和 UI
    this.setAttribute('value', raw);
    this._updateButtons();
    this._updateCollapsedLabel();
  }

  _onChange() {
    // 确保最终值在范围内，然后触发 change
    let raw = parseInt(this._input.value);
    if (Number.isNaN(raw)) raw = this.min;
    if (raw > this.max) raw = this.max;
    if (raw < this.min) raw = this.min;
    if (String(this._input.value) !== String(raw)) this._input.value = raw;
    this.setAttribute('value', raw);
    this._updateButtons();
    this._updateCollapsedLabel();
    this._fireChange();
  }

  _updateButtons() {
    this._decreaseBtn.disabled = this.disabled || this.value <= this.min;
    this._increaseBtn.disabled = this.disabled || this.value >= this.max;
  }

  _updateCollapsedLabel() {
    if (!this._collapsedLabel) return;
    const v = this.value || parseInt(this._input.value) || 0;
    this._collapsedLabel.textContent = `×${v}`;
  }

  _fireChange() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this.value },
      bubbles: true,
      composed: true
    }));
  }
}

// 注册自定义元素
if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}

class SceneCheckbox extends HTMLElement {
  static get observedAttributes() { return ['checked']; }
  constructor() { super(); }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    if (name === 'checked') {
      this.checked = newVal !== null; // attribute 存在就是 true，否则 false
    }
  }

  connectedCallback() {
    // 让 attribute（如 <scene-checkbox checked>）初始化生效
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.checked = this.hasAttribute('checked');
    this.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.checked = !this.checkbox.checked;
      this.dispatchEvent(new CustomEvent('change', { detail: this.checked, bubbles: true }));
      this.dispatchEvent(new CustomEvent('input', { detail: this.checked, bubbles: true }));
    });
  }

  get checked() {
    return this.checkbox ? this.checkbox.checked : false;
  }
  set checked(val) {
    const boolVal = (val === true || val === 'true' || val === '' || val === 1);

    if (this.checkbox) this.checkbox.checked = boolVal;
    // 保持 prop/attribute 同步
    if (boolVal) {
      this.setAttribute('checked', '');
    } else {
      this.removeAttribute('checked');
    }
  }
}
if (!customElements.get('scene-checkbox')) {
  customElements.define('scene-checkbox', SceneCheckbox);
}

const SHOPIFY_EVENTS = {
  sectionLoad: 'shopify:section:load',
  sectionUnload: 'shopify:section:unload',
  sectionSelect: 'shopify:section:select',
  sectionDeselect: 'shopify:section:deselect',
  blockSelect: 'shopify:block:select',
  blockDeselect: 'shopify:block:deselect',
};

const SECTION_IDS = {
  prefix: '#shopify-section-',
  mainQuickView: 'main-qv-mutil',
};

/**
 * T4SThemeSP.ProductItem - 产品项管理模块
 * 负责产品卡片的渲染、交互和状态管理
 */
function LuteProductItem() {
  // ==================== 配置管理 ====================

  /**
   * 从全局配置中提取并存储所有配置项
   */
  const CONFIG = {
    // 时间相关
    currentTimestamp: T4Sconfigs.nowTimestamp,
    newBadgeDays: T4Sconfigs.new_day_int,

    // 图片相关
    showSecondImage: T4Sconfigs.show_img,
    secondImageTemplate: T4Sconfigs.img2,

    // 功能开关
    enableQuickShop: T4Sconfigs.enable_quickshop,
    withinCollection: T4Sconfigs.within_cat,
    showQuantity: T4Sconfigs.show_qty,
    currentProduct: T4Sconfigs.pr_curent,

    // 徽章配置
    badges: {
      useSale: T4Sconfigs.use_sale_badge,
      saleStyle: T4Sconfigs.label_sale_style, // '2'=百分比, '3'=金额
      usePreorder: T4Sconfigs.use_preorder_badge,
      useNew: T4Sconfigs.use_new_badge,
      useSoldout: T4Sconfigs.use_soldout_badge,
      useCustom: T4Sconfigs.use_custom_badge,
    },

    // 色板配置
    swatches: {
      enableLimit: T4Sconfigs.swatch_limit,
      clickMode: T4Sconfigs.swatch_click,
      fixedNumber: T4Sconfigs.swatch_num,
      itemStyle: T4Sconfigs.sw_item_style,
      maxDefault: 2, // 默认最大显示数量
    },

    // 评论系统
    reviewApp: T4Sconfigs.app_review, // '1'=SPR, '6'=Smart, '8'=Air
  };

  /**
   * 文本字符串配置
   */
  const STRINGS = {
    // 按钮文本
    preOrder: T4SProductStrings.preOrder,
    readMore: T4SProductStrings.readMore,
    selectOption: T4SProductStrings.selectOption,
    quickShop: T4SProductStrings.quickShop,
    preview: T4SProductStrings.preView,

    // 徽章文本
    badges: {
      sale: T4SProductStrings.badgeSale,
      new: T4SProductStrings.badgeNew,
      preOrder: T4SProductStrings.badgepreOrder,
      soldout: T4SProductStrings.badgeSoldout,
      savePercent: T4SProductStrings.badgeSavePercent,
    },

    // 色板文本
    swatchLimit: T4SProductStrings.swatch_limit,
    swatchLimitLess: T4SProductStrings.swatch_limit_less,
  };

  /**
   * DOM 选择器常量
   */
  const SELECTORS = {
    productOptions: '[data-product-options]',
    productBadge: '[data-product-badge]',
    colorOptions: '[data-color-options]',
    productImage: '[data-pr-img]',
    productLink: '[data-pr-href]',
    productSizes: '.t4s-product-sizes',
    addToCartButton: '[data-atc-selector]',

    // 替换占位符
    replaceQuickview: '[data-replace-quickview]',
    replaceCompare: '[data-replace-compare]',
    replaceWishlist: '[data-replace-wishlist]',
    replaceAtc: '[data-replace-atc]',

    // 色板相关
    swatchItem: '.t4s-pr-color__item:not(.is-swatch--current):not([data-img="none"]):not(.is--colors-more)',

    // Collection URL
    collectionUrl: '[data-collection-url]',
    productHref: '[data-pr-href]:not(.is--href-replaced)',

    // 观察器
    resizeObserver: '[data-t4s-resizeobserver]',
  };

  /**
   * CSS 类名常量
   */
  const CLASSES = {
    loading: 'is--loading',
    opened: 'is-t4s--opended',
    calculated: 'is-t4s--calced',
    limited: 'is-t4s--limit',
    productCreated: 'initProducts__enabled',
    hrefReplaced: 'is--href-replaced',
    colorSelected: 't4s-colors-selected',
    swatchSelected: 'is-swatch--selected',
    sizesSoldOut: 't4s-product-sizes--sold-out',
  };

  /**
   * 数据属性常量
   */
  const ATTRIBUTES = {
    tooltip: 'data-tooltip',
    productOptions: 'data-product-options',
    colorOptions: 'data-color-options',
    collectionUrl: 'data-collection-url',
  };

  // ==================== 徽章管理模块 ====================

  /**
   * 渲染产品徽章
   * @param {jQuery} $element - 产品容器元素
   * @param {Object} productData - 产品数据对象
   */
  function renderBadges($element, productData) {
    const $badgeContainer = $element.find(SELECTORS.productBadge);
    const badgeTypes = ($badgeContainer.attr('data-sort') || '').replace(/ /g, '').split(',');

    if (badgeTypes.length === 0 || $badgeContainer.length === 0) {
      return;
    }

    let badgesHtml = '';
    const hideWhenQuickShop = !productData.unQuickShopInline;

    badgeTypes.forEach((badgeType) => {
      switch (badgeType) {
        case 'sale':
          badgesHtml += renderSaleBadge(productData, hideWhenQuickShop);
          break;
        case 'preOrder':
          badgesHtml += renderPreorderBadge(productData, hideWhenQuickShop);
          break;
        case 'new':
          badgesHtml += renderNewBadge(productData);
          break;
        case 'soldout':
          badgesHtml += renderSoldoutBadge(productData, hideWhenQuickShop);
          break;
        default:
          badgesHtml += renderCustomBadges(productData);
      }
    });

    $badgeContainer.html(badgesHtml);
  }

  /**
   * 渲染折扣徽章
   */
  function renderSaleBadge(productData, hideWhenQuickShop) {
    const comparePrice = productData.compare_at_price;
    const price = productData.price;

    // 如果没有折扣或不显示折扣徽章
    if (comparePrice <= price || !CONFIG.badges.useSale) {
      return hideWhenQuickShop ? '<span data-badge-sale class="t4s-badge-item t4s-badge-sale" hidden></span>' : '';
    }

    let badgeText;
    if (CONFIG.badges.saleStyle === '2') {
      // 显示百分比
      const discountPercent = (100 * (comparePrice - price)) / comparePrice;
      badgeText = STRINGS.badges.savePercent.replace('[sale]', Math.round(discountPercent));
    } else if (CONFIG.badges.saleStyle === '3') {
      // 显示金额
      const discountAmount = comparePrice - price;
      badgeText = T4SThemeSP.Currency.formatMoney(discountAmount);
    } else {
      // 显示默认文本
      badgeText = STRINGS.badges.sale;
    }

    return `<span data-badge-sale class="t4s-badge-item t4s-badge-sale">${badgeText}</span>`;
  }

  /**
   * 渲染预购徽章
   */
  function renderPreorderBadge(productData, hideWhenQuickShop) {
    if (!productData.isPreoder || !CONFIG.badges.usePreorder) {
      return hideWhenQuickShop
        ? `<span data-badge-preorder class="t4s-badge-item t4s-badge-preorder" hidden>${STRINGS.badges.preOrder}</span>`
        : '';
    }

    return `<span data-badge-preorder class="t4s-badge-item t4s-badge-preorder">${STRINGS.badges.preOrder}</span>`;
  }

  /**
   * 渲染新品徽章
   */
  function renderNewBadge(productData) {
    if (!CONFIG.badges.useNew) {
      return '';
    }

    const timeDiff = CONFIG.currentTimestamp - productData.dateStart;
    const hours = Math.floor(timeDiff / 3600);
    const days = Math.floor(hours / 24);

    if (days >= CONFIG.newBadgeDays) {
      return '';
    }

    return `<span class="t4s-badge-item t4s-badge-new">${STRINGS.badges.new}</span>`;
  }

  /**
   * 渲染售罄徽章
   */
  function renderSoldoutBadge(productData, hideWhenQuickShop) {
    if (productData.available || !CONFIG.badges.useSoldout) {
      return hideWhenQuickShop
        ? `<span data-badge-soldout class="t4s-badge-item t4s-badge-soldout" hidden>${STRINGS.badges.soldout}</span>`
        : '';
    }

    return `<span data-badge-soldout class="t4s-badge-item t4s-badge-soldout">${STRINGS.badges.soldout}</span>`;
  }

  /**
   * 渲染自定义徽章
   */
  function renderCustomBadges(productData) {
    if (!productData.customBadge || !CONFIG.badges.useCustom) {
      return '';
    }

    let html = '';
    productData.customBadge.forEach((badge, index) => {
      const handle = productData.customBadgeHandle[index];
      html += `<span class="t4s-badge-item t4s-badge-custom t4s-badge-${handle}">${badge}</span>`;
    });

    return html;
  }

  // ==================== 色板管理模块 ====================

  /**
   * 渲染颜色选项/色板
   * @param {jQuery} $element - 产品容器元素
   * @param {string} productUrl - 产品URL
   */
  function renderSwatches($element, productUrl) {
    const $swatchContainer = $element.find(`[${ATTRIBUTES.colorOptions}]`);

    if ($swatchContainer.length === 0) {
      return;
    }

    const swatchData = parseJSON($swatchContainer.attr(ATTRIBUTES.colorOptions));
    const {
      color_variants: colorVariants,
      color_variants_avai: availableColors,
      color_variants_handle: colorHandles,
      img_options: imageOptions,
      img_variants: variantImages,
      id_variants: variantIds,
      id_images: imageIds = [],
      img_ratios: imageRatios,
    } = swatchData;

    let swatchesHtml = '';
    const hasVariantImages = variantImages.length > 0;
    const hasUnavailableColors = colorVariants.length !== availableColors.length;

    // 为每个颜色生成色板项
    colorVariants.forEach((colorName, index) => {
      const imageIndex = imageOptions.indexOf(colorName);
      const variantImage = hasVariantImages ? variantImages[imageIndex] : 'nt94';
      const finalImage = variantImage || 'nt94';

      // 生成图片路径
      const imageDataAttr = finalImage !== 'nt94' ? T4SThemeSP.Images.lazyloadImagePath(finalImage) : 'none';

      // 生成背景图片属性（用于特定样式）
      const bgAttr =
        finalImage !== 'nt94' && CONFIG.swatches.itemStyle === '2'
          ? `data-bg="${T4SThemeSP.Images.getNewImageUrl(finalImage, 100)}"`
          : '';

      // 检查是否售罄
      const soldOutClass =
        hasUnavailableColors && availableColors.indexOf(colorName) < 0 ? ' t4s-pr-color--sold-out' : '';

      swatchesHtml += `
          <span 
            data-imgid="${imageIds[imageIndex] || '0'}"
            class="t4s-pr-color__item${soldOutClass}"
            data-vid="${variantIds[imageIndex]}"
            data-tooltip="top"
            data-img="${imageDataAttr}"
            data-ratio="${imageRatios[imageIndex] || ''}">
            <span class="t4s-pr-color__name">${colorName}</span>
            <span class="t4s-pr-color__value bg_color_${colorHandles[index]} lazyloadt4s" ${bgAttr}></span>
          </span>`;
    });

    // 如果颜色数量超过限制，添加"更多"链接
    if (colorVariants.length > CONFIG.swatches.maxDefault && CONFIG.swatches.enableLimit) {
      const swatchLimitLabel = (STRINGS.swatchLimit && STRINGS.swatchLimit.trim()) || 'Show More Colors';
      swatchesHtml += `
          <span class="t4s-pr-color__item is--colors-more" data-tooltip="top-end">
            <span class="t4s-pr-color__name">${swatchLimitLabel}</span>
            <a href="${productUrl}" class="t4s-pr-color__value bg_color_limit4s" aria-label="${swatchLimitLabel}"></a>
          </span>`;
    }

    $swatchContainer.html(swatchesHtml);

    // 如果存在尺码选择器，预加载产品JSON数据
    if ($element.find(SELECTORS.productSizes).length > 0) {
      preloadProductJSON($element, swatchData);
    }
  }

  /**
   * 预加载产品JSON数据（用于尺码联动）
   */
  function preloadProductJSON($element, swatchData) {
    const productOptions = $element.closest(SELECTORS.productOptions).data('product-options');
    const productId = productOptions.id;

    // 如果已经加载过，跳过
    if (typeof T4sFunc.psjson_lib[productId] !== 'undefined') {
      return;
    }

    // 异步加载产品数据
    $.ajax({
      url: Shopify.routes.root + 'products/' + productOptions.handle + '.js',
      type: 'GET',
      dataType: 'json',
    })
      .done(function (data) {
        T4sFunc.psjson_lib[productId] = data;
      })
      .fail(function () {
        console.warn('Failed to load product data:', productOptions.handle);
      });
  }

  /**
   * 重新计算色板显示限制
   * @param {boolean} forceRecalc - 是否强制重新计算
   */
  function recalculateSwatchLimit(forceRecalc = false) {
    if (!CONFIG.swatches.enableLimit) {
      return;
    }

    const selector = forceRecalc
      ? `[${ATTRIBUTES.colorOptions}]`
      : `[${ATTRIBUTES.colorOptions}]:not(.${CLASSES.calculated})`;

    if (CONFIG.swatches.fixedNumber) {
      // 固定数量模式
      calculateFixedSwatchLimit(selector);
    } else {
      // 动态宽度模式
      calculateDynamicSwatchLimit(selector);
    }
  }

  /**
   * 计算固定数量的色板限制
   */
  function calculateFixedSwatchLimit(selector) {
    fastdomT4s.measure(function () {
      $(selector).each(function () {
        const $container = $(this);
        const totalSwatches = $container.find('.t4s-pr-color__item').length - 1;
        const limit = CONFIG.swatches.fixedNumber;
        const overflow = totalSwatches - limit;

        fastdomT4s.mutate(function () {
          $container.addClass(CLASSES.calculated).removeClass(CLASSES.limited);
          $container.find('.is-color--limit').removeClass('is-color--limit');

          if (overflow > 0) {
            $container.addClass(CLASSES.limited);
            $container
              .find('.t4s-pr-color__item')
              .eq(limit - 1)
              .addClass('is-color--limit');
            $container
              .attr('data-limit', totalSwatches)
              .attr('style', `--text: "+${overflow}"; --text2: "-${overflow}"`);
          }
        });
      });
    });
  }

  /**
   * 计算动态宽度的色板限制
   */
  function calculateDynamicSwatchLimit(selector) {
    fastdomT4s.measure(function () {
      $(selector).each(function () {
        const $container = $(this);
        const $items = $container.find('.t4s-pr-color__item');
        const itemWidth = $items.outerWidth(true);
        const totalSwatches = $items.length - 1;
        const containerWidth = $container.outerWidth();
        const visibleCount = Math.floor(containerWidth / itemWidth);
        let overflow = totalSwatches - visibleCount;

        fastdomT4s.mutate(function () {
          $container.addClass(CLASSES.calculated).removeClass(CLASSES.limited);
          $container.find('.is-color--limit').removeClass('is-color--limit');

          if (overflow > 0 && overflow !== totalSwatches) {
            overflow += 1;
            $container.addClass(CLASSES.limited);
            $container
              .find('.t4s-pr-color__item')
              .eq(visibleCount - 2)
              .addClass('is-color--limit');
            $container
              .attr('data-limit', visibleCount)
              .attr('style', `--text: "+${overflow}"; --text2: "-${overflow}"`);
          }
        });
      });
    });
  }

  /**
   * 处理色板点击/悬停事件
   * @param {jQuery} $swatchItem - 被选中的色板项
   */
  function handleSwatchSelection($swatchItem) {
    const $productContainer = $swatchItem.closest(SELECTORS.productOptions);
    const productOptions = $productContainer.data('product-options');
    const imageId = $swatchItem.data('imgid');

    if (imageId === '0') {
      // 没有关联特定图片ID，更新主图
      updateMainImage($swatchItem, $productContainer, productOptions);
    } else {
      // 有关联图片ID，切换轮播图
      updateCarouselImage($swatchItem, $productContainer, imageId);
    }
  }

  /**
   * 更新产品主图
   */
  function updateMainImage($swatchItem, $productContainer, productOptions) {
    const imageSrc = $swatchItem.data('img');
    const variantId = $swatchItem.data('vid');
    const $image = $productContainer.find(SELECTORS.productImage);
    const $link = $productContainer.find(SELECTORS.productLink);
    const currentUrl = $link.attr('href');

    // 更新选中状态
    $swatchItem
      .closest(`[${ATTRIBUTES.colorOptions}]`)
      .find(`.${CLASSES.swatchSelected}`)
      .removeClass(CLASSES.swatchSelected);
    $swatchItem.addClass(CLASSES.swatchSelected);
    $productContainer.addClass(CLASSES.colorSelected);

    // 更新图片
    $image.attr('data-srcset', imageSrc);

    // 更新URL（添加variant参数）
    if (CONFIG.currentProduct !== '1' && typeof variantId !== 'undefined') {
      const newUrl = updateUrlWithVariant(currentUrl, variantId);
      $link.attr('href', newUrl);

      // 更新尺码选择器的库存状态
      updateSizeAvailability($productContainer, productOptions, variantId);
    }
  }

  /**
   * 更新轮播图到指定图片
   */
  function updateCarouselImage($swatchItem, $productContainer, imageId) {
    const $carousel = $productContainer.find('.flickityt4s-enabled[data-product-img-carousel]');

    if ($carousel.length === 0) {
      return;
    }

    const $targetSlide = $carousel.find(`[data-product-img-slide="${imageId}"]`);
    const slideIndex = $targetSlide.index();

    $carousel.flickityt4s('select', slideIndex);
  }

  /**
   * 更新URL中的variant参数
   */
  function updateUrlWithVariant(url, variantId) {
    if (/variant=/.test(url)) {
      return url.replace(/(variant=)[^&]+/, '$1' + variantId);
    } else if (/\?/.test(url)) {
      return url.concat('&variant=').concat(variantId);
    } else {
      return url.concat('?variant=').concat(variantId);
    }
  }

  /**
   * 更新尺码选择器的库存状态
   */
  function updateSizeAvailability($productContainer, productOptions, variantId) {
    const productId = productOptions.id;
    const productData = T4sFunc.psjson_lib[productId];

    if (typeof productData === 'undefined') {
      return;
    }

    const $sizeContainer = $productContainer.find(SELECTORS.productSizes);
    const $sizeItems = $sizeContainer.find('>span');
    const variants = productData.variants;
    const selectedVariant = variants.find((v) => v.id === variantId);

    if (!selectedVariant) {
      return;
    }

    // 获取同颜色的所有尺码变体
    const colorIndex = productOptions.index_color;
    const sameColorVariants = variants.filter((v) => v[colorIndex] === selectedVariant[colorIndex]);

    // 更新尺码的售罄状态
    $sizeItems.removeClass(CLASSES.sizesSoldOut);
    let availableCount = 0;

    sameColorVariants.forEach((variant, index) => {
      if (variant.available) {
        availableCount++;
      } else {
        $sizeItems.eq(index).addClass(CLASSES.sizesSoldOut);
      }
    });

    $sizeContainer.attr('data-size', availableCount);
  }

  // ==================== Collection URL 管理 ====================

  /**
   * 更新产品链接，添加 collection 上下文
   * 在 collection 页面内保持浏览上下文
   */
  function updateCollectionUrls() {
    if (!CONFIG.withinCollection || $(SELECTORS.collectionUrl).length === 0) {
      return;
    }

    $(SELECTORS.collectionUrl).each(function () {
      const $container = $(this);
      const collectionUrl = $container.attr(ATTRIBUTES.collectionUrl);

      if (!$.trim(collectionUrl).length) {
        return;
      }

      $container.find(SELECTORS.productHref).each(function () {
        const $link = $(this);
        let productPath = $link.attr('href');

        // 提取产品路径（/products/之后的部分）
        productPath = productPath.split('/products/')[1];
        productPath = '/products/' + productPath;

        // 添加 collection 上下文
        const newHref = collectionUrl + productPath;
        $link.attr('href', newHref).addClass(CLASSES.hrefReplaced);
      });
    });
  }

  // ==================== 按钮和功能替换 ====================

  /**
   * 渲染第二张图片
   * @param {jQuery} $element - 产品容器
   * @param {string} image2Url - 第二张图片URL
   * @param {string} imageAlt - 图片alt文本
   */
  function renderSecondImage($element, image2Url, imageAlt) {
    if (CONFIG.showSecondImage !== '2' || !image2Url) {
      $element.find('[data-replace-img2]').replaceWith('');
      return;
    }

    const imageHtml = CONFIG.secondImageTemplate.replace('image_src', image2Url).replace('image_alt', imageAlt);

    $element.find('.t4s-product-img').addClass('is-show-img2');
    $element.find('[data-replace-img2]').replaceWith(imageHtml);
  }

  /**
   * 替换功能按钮（快速查看、比较、心愿单、加入购物车）
   * @param {jQuery} $element - 产品容器
   * @param {Object} productData - 产品数据
   * @param {string} productUrl - 产品URL
   */
  function replaceFunctionalButtons($element, productData, productUrl) {
    const buttonTemplates = $('#btns_pr_temp').html();

    if (!buttonTemplates) {
      return;
    }

    const templates = buttonTemplates.replace(/#t4s_pr_url/g, productUrl).split('[split_t4snt]');

    const productId = productData.id;
    const productHandle = productData.handle;

    // 替换快速查看按钮
    replaceQuickviewButtons($element, templates[0], productId);

    // 替换比较按钮
    replaceCompareButtons($element, templates[1], productId, productHandle);

    // 替换心愿单按钮
    replaceWishlistButtons($element, templates[2], productId, productHandle);

    // 替换加入购物车按钮
    replaceAddToCartButton($element, templates[3], templates[4], productData);
  }

  /**
   * 替换快速查看按钮
   */
  function replaceQuickviewButtons($element, template, productId) {
    $element.find(SELECTORS.replaceQuickview).each(function () {
      const $btn = $(this);
      const tooltip = $btn.attr(ATTRIBUTES.tooltip) || '';
      const html = template
        .replace(`${ATTRIBUTES.tooltip}="`, `${ATTRIBUTES.tooltip}="${tooltip}`)
        .replace('id_nt_94', productId);
      $btn.replaceWith(html);
    });
  }

  /**
   * 替换比较按钮
   */
  function replaceCompareButtons($element, template, productId, productHandle) {
    $element.find(SELECTORS.replaceCompare).each(function () {
      const $btn = $(this);
      const tooltip = $btn.attr(ATTRIBUTES.tooltip) || '';
      const html = template
        .replace(`${ATTRIBUTES.tooltip}="`, `${ATTRIBUTES.tooltip}="${tooltip}`)
        .replace('id_nt_94', productId)
        .replace('handle_nt_94', productHandle);
      $btn.replaceWith(html);
    });
  }

  /**
   * 替换心愿单按钮
   */
  function replaceWishlistButtons($element, template, productId, productHandle) {
    $element.find(SELECTORS.replaceWishlist).each(function () {
      const $btn = $(this);
      const tooltip = $btn.attr(ATTRIBUTES.tooltip) || '';
      const html = template
        .replace(`${ATTRIBUTES.tooltip}="`, `${ATTRIBUTES.tooltip}="${tooltip}`)
        .replace('id_nt_94', productId)
        .replace('handle_nt_94', productHandle);
      $btn.replaceWith(html);
    });
  }

  /**
   * 替换加入购物车按钮
   */
  function replaceAddToCartButton($element, atcTemplate, qtyTemplate, productData) {
    $element.find(SELECTORS.replaceAtc).each(function () {
      const $btn = $(this);
      const hasQty = $btn.is('[data-has-qty]');
      const tooltip = $btn.attr(ATTRIBUTES.tooltip) || '';
      let html = atcTemplate
        .replace(`${ATTRIBUTES.tooltip}="`, `${ATTRIBUTES.tooltip}="${tooltip}`)
        .replace('id_nt_94', productData.id);

      const $atcBtn = $(html);
      const $text = $atcBtn.find('.t4s-text-pr');
      const $icon = $atcBtn.find('.t4s-svg-pr-icon use');

      // 根据产品状态设置按钮
      if (productData.isExternal) {
        // 外部产品
        $atcBtn.attr('href', productData.external_link).attr('target', '_blank');
        $text.text(productData.external_title);
        $icon.attr('xlink:href', '#t4s-icon-external');
      } else if (productData.available) {
        // 可购买
        if (productData.isGrouped) {
          $text.text(STRINGS.preview);
        } else if (productData.isDefault) {
          if (productData.isPreoder) {
            $text.text(STRINGS.preOrder);
          }
          $atcBtn.attr({
            'data-action-atc': '',
            'data-variant-id': productData.VariantFirstID,
            'data-qty': productData.cusQty || 1,
          });

          // 添加数量选择器
          if (CONFIG.showQuantity && hasQty) {
            const qtyHtml = qtyTemplate
              .replace('max="9999"', `max="${productData.maxQuantity}"`)
              .replace('min="1"', `min="${productData.cusQty || 1}"`)
              .replace(/id_nt_94/g, productData.id);
            html = `<div class="t4s-product-atc-qty">${qtyHtml}${$atcBtn[0].outerHTML}</div>`;
          } else {
            html = $atcBtn[0].outerHTML;
          }
        } else {
          // 需要选择选项
          if (CONFIG.enableQuickShop && productData.unQuickShopInline) {
            $atcBtn.attr('data-action-quickshop', '');
            $text.text(STRINGS.quickShop);
          } else {
            $text.text(STRINGS.selectOption);
          }
          html = $atcBtn[0].outerHTML;
        }
      } else {
        // 售罄
        $text.text(STRINGS.readMore);
        $icon.attr('xlink:href', '#t4s-icon-link');
        html = $atcBtn[0].outerHTML;
      }

      $btn.replaceWith(html);

      // 为 QuickShop inline 模式准备事件
      if (!productData.unQuickShopInline) {
        $element.one('replace:btnAtc', function () {
          const $newBtn = $element.find(SELECTORS.addToCartButton);
          $newBtn.attr({
            'data-action-atc': '',
            'data-variant-id': productData.VariantFirstID,
            'data-qty': productData.cusQty || 1,
          });

          if (CONFIG.showQuantity && hasQty && $newBtn[0]) {
            const qtyHtml = qtyTemplate
              .replace('max="9999"', `max="${productData.maxQuantity}"`)
              .replace('min="1"', `min="${productData.cusQty || 1}"`);
            $newBtn.replaceWith(`<div class="t4s-product-atc-qty">${qtyHtml}${$newBtn[0].outerHTML}</div>`);
          }
        });
      }
    });
  }

  /**
   * 初始化 QuickShop inline 模式
   */
  function initQuickShopInline(productElement, $element, productData) {
    if (
      productData.unQuickShopInline ||
      $element.hasClass(CLASSES.productCreated) ||
      productData.isGrouped ||
      productData.isExternal
    ) {
      return;
    }

    const $inlineContainer = $element.find('[data-qs-inl]');

    if ($inlineContainer.hasClass('lazyloadt4sed')) {
      new T4SThemeSP.Product(productElement);
    } else {
      $inlineContainer.one('lazyincluded', function () {
        new T4SThemeSP.Product(productElement);
      });
    }
  }

  // ==================== 主初始化函数 ====================

  /**
   * 初始化所有未创建的产品项
   * 这是产品项渲染的主入口函数
   */
  function initializeProducts() {
    // 更新 collection URLs
    updateCollectionUrls();

    // 遍历所有未初始化的产品项
    $('[data-product-options]:not(.is-t4s-pr-created)').each(function () {
      const $productElement = $(this);
      const productData = parseJSON($productElement.attr('data-product-options'));
      const productUrl = $productElement.find('[data-pr-href]').attr('href');

      // 确保 unQuickShopInline 是布尔值
      if (typeof productData.unQuickShopInline !== 'boolean') {
        productData.unQuickShopInline = true;
      }

      // 渲染第二张图片
      renderSecondImage($productElement, productData.image2, productData.alt);

      // 替换功能按钮
      replaceFunctionalButtons($productElement, productData, productUrl);

      // 渲染徽章
      renderBadges($productElement, productData);

      // 渲染色板
      renderSwatches($productElement, productUrl);

      // 重新计算色板限制
      recalculateSwatchLimit();

      // 初始化 QuickShop inline
      initQuickShopInline(this, $productElement, productData);

      // 标记为已创建
      $productElement.addClass('is-t4s-pr-created');
    });

    // 初始化其他组件
    T4SThemeSP.Tooltip();
    T4SThemeSP.Compare.updateAll();
    T4SThemeSP.Wishlist.updateAll();
  }

  // ==================== 快速查看/快速购物模块 ====================

  /**
   * 快速查看/快速购物缓存
   * 格式：{ 'main-qv{productId}': html, 'main-qs{productId}': html }
   */
  const quickViewCache = {};
  const $body = $('body');
  const $html = $('html');
  const $doucment = $(document);

  function _updateSwatchSelector($quickView) {
    const selectItem = window.ltQv.selected
    if (!selectItem) return;
    const $productGroupForm = $quickView.find("product-group-form .t4s-grouped__form");
    const $prodItem = $quickView.find(`[data-groups-pr-item][data-pid="${selectItem.id}"]`);
    $prodItem.attr('data-edit', window.ltQv.isEdit)
    const $productSelect = $prodItem.find(`lt-select`);
    const $swatchItem = $prodItem.find(`[data-groups-qty-value]`)

    $productSelect.val(window.ltQv.isEdit ? selectItem.vid : '')
    $swatchItem.val(window.ltQv.isEdit ? selectItem.quantity : 1)
    $productGroupForm.trigger(ProductGroupForm.EVENTS.change);
  }

  /**
   * 初始化快速查看和快速购物功能
   */
  function initQuickViewShop() {
    // 如果启用数量选择器，添加相应类名
    if (CONFIG.showQuantity) {
      $html.addClass('t4s-pr-item-has-qty');
    }

    /**
     * 处理快速查看/快速购物点击事件
     */
    $body.on('click', '[data-action-quickview-new], [data-action-quickshop]', function (event) {
      event.preventDefault();

      const $button = $(this);

      if ($button.hasClass(CLASSES.loading)) {
        return;
      }

      const productUrl = $button.attr('href');
      const isQuickView = $button.is('[data-action-quickview-new]');
      const sectionId = isQuickView ? window.mainQVSectionId : 'main-qs';
      const openingClass = isQuickView ? 't4s-opening-qv' : 't4s-opening-qs';
      const productId = $button.data('id');
      const cacheKey = sectionId + productId;
      const cachedHtml = quickViewCache[cacheKey];
      const initCallback = isQuickView ? initQuickView : initQuickShop;
      const bid = $button.data('bid') || '';
      // 设置全局变量（用于购物车编辑模式）
      T4SThemeSP.isEditCartReplace = $button.data('edit') === '0';
      T4SThemeSP.iDVariantEdit = productId;
      T4SThemeSP.keyVariantEdit = $button.data('key');

      // 如果有缓存，直接使用
      if (cachedHtml) {
        T4SThemeSP.NTpopupInline(cachedHtml, sectionId, initCallback, openingClass);
        $body.trigger('modalt4s:opened');
        return;
      }

      // 如果是购物车编辑模式，显示加载状态
      if (T4SThemeSP.isEditCartReplace) {
        handleCartEditMode($button);
      } else {
        $button.addClass(CLASSES.loading);
      }

      // 构建请求URL
      const separator = productUrl.indexOf('?') > -1 || productUrl.indexOf('&') > -1 ? '&' : '/?';
      const fetchUrl = `${productUrl}${separator}section_id=${sectionId}&fetch=1&style=1&bid=${bid}`;

      // 获取内容
      fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          $button.removeClass(CLASSES.loading);
          $doucment.trigger('cart:updated');

          // 处理返回的 HTML（设计模式下需要从 template 中提取）
          const finalHtml = window.IsDesignMode ? $(html).find('template').html() : $(html).html();
          // 显示弹窗
          T4SThemeSP.NTpopupInline(finalHtml, sectionId, initCallback, openingClass);
          $body.trigger('modalt4s:opened');

          // 缓存内容
          quickViewCache[cacheKey] = finalHtml;
        })
        .catch((error) => {
          $button.removeClass(CLASSES.loading);
          $doucment.trigger('cart:updated');
          console.error('Failed to load quick view/shop:', error);
        });
    });
  }

  /**
   * 快速购物初始化回调
   */
  function initQuickShop() {
    const $quickShop = $('.t4s-product-quick-shop:not(.' + CLASSES.productCreated + ')');
    $quickShop.addClass(CLASSES.productCreated);

    new T4SThemeSP.Product($quickShop[0]);
    T4SThemeSP.PopupMFP();

    if (window.Shopify && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    T4SThemeSP.Wishlist.updateAll();
    T4SThemeSP.Compare.updateAll();
    reloadReviewBadges();
    T4SThemeSP.Tooltip();
    $body.trigger('currency:update');
  }

  /**
   * 快速查看初始化回调
   */
  function initQuickView() {
    const $quickView = $('.t4s-product-quick-view');
    const $productFeatured = $quickView.find('[data-product-featured]:not(.' + CLASSES.productCreated + ')');
    $productFeatured.addClass(CLASSES.productCreated);

    const $mainMedia = $quickView.find('[data-main-media]');
    if ($mainMedia.hasClass('flickityt4s') && !$mainMedia.hasClass('flickityt4s-enabled')) {
      $mainMedia[0].flickityt4s = new T4SThemeSP.Carousel($mainMedia[0]);
    }

    new T4SThemeSP.Product($productFeatured[0]);
    T4SThemeSP.PopupMFP();

    _updateSwatchSelector($quickView);
    // T4SThemeSP.initGroupsProduct();

    if (window.Shopify && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    // T4SThemeSP.Wishlist.updateAll();
    // T4SThemeSP.Compare.updateAll();
    // reloadReviewBadges();
    T4SThemeSP.Tooltip();
    $body.trigger('currency:update');
  }

  async function openQuickViewShop({ productUrl, productId, sectionId, bid, handle, variantId }) {
    const openingClass = 't4s-opening-qv';
    const cacheKey = sectionId + productId;
    const cachedHtml = quickViewCache[cacheKey];
    const initCallback = initQuickView;

    // 如果有缓存，直接使用
    if (cachedHtml) {
      T4SThemeSP.NTpopupInline(cachedHtml, sectionId, initCallback, openingClass);
      $body.trigger('modalt4s:opened');
      return;
    }

    // 构建请求URL
    const separator = productUrl.indexOf('?') > -1 || productUrl.indexOf('&') > -1 ? '&' : '/?';
    let fetchUrl = `${productUrl}${separator}section_id=${sectionId}&fetch=1&style=1&bid=${bid}&handle=${handle}`;
    if (variantId) {
      fetchUrl += `&variant=${variantId}`;
    }

    // 获取内容
    try {
      const modalHtml = await fetch(fetchUrl).then((response) => response.text());
      $doucment.trigger('cart:updated');

      // 处理返回的 HTML（设计模式下需要从 template 中提取）
      const finalHtml = window.IsDesignMode ? $(modalHtml).find('template').html() : $(modalHtml).html();
      // 显示弹窗
      T4SThemeSP.NTpopupInline(finalHtml, sectionId, initCallback, openingClass);
      $body.trigger('modalt4s:opened');

      // 缓存内容
      quickViewCache[cacheKey] = finalHtml;
    } catch (error) {
      $doucment.trigger('cart:updated');
      console.error('Failed to load quick view/shop:', error);
    }
  }

  /**
   * 处理购物车编辑模式的 UI 反馈
   */
  function handleCartEditMode($button) {
    const $cartItem = $button.closest('[data-cart-item]');
    const $cartWrapper = $button.closest('[data-cart-wrapper]');
    const $loadingBar = $cartItem.find('.t4s-cart-ld__bar');
    const $spinner = $loadingBar.find('.t4s-cart-spinner');

    $cartWrapper.addClass('is--contentUpdate');
    $cartItem.addClass('is--update');
    $loadingBar.removeAttr('hidden');
    $spinner.removeAttr('hidden');

    $(window).on('cart:updated', function handleCartUpdate(event, status = 'success') {
      $cartWrapper.removeClass('is--contentUpdate');
      $(window).off('cart:updated', handleCartUpdate);
      $cartItem.removeClass('is--update');
      $loadingBar.attr('hidden', '');
      $spinner.attr('hidden', '');

      if (status === 'success') {
        $(document).trigger('modalt4s:closed');
      }
    });
  }

  // ==================== 色板交互 ====================

  /**
   * 处理色板"更多"链接的点击
   */
  function initSwatchMoreClick() {
    if (!CONFIG.swatches.enableLimit || CONFIG.swatches.clickMode === '2') {
      return;
    }

    $(document).on('click', '.t4s-pr-color__item.is--colors-more>a', function (event) {
      event.preventDefault();

      const $limitedContainer = $(this).closest('.' + CLASSES.limited);
      const $label = $(this).siblings('.t4s-pr-color__name');

      if ($limitedContainer.hasClass(CLASSES.opened)) {
        $limitedContainer.removeClass(CLASSES.opened);
        $label.text(STRINGS.swatchLimit);
      } else {
        $limitedContainer.addClass(CLASSES.opened);
        $label.text(STRINGS.swatchLimitLess);
      }
    });
  }

  /**
   * 初始化色板点击/悬停事件
   */
  function initSwatchClickHover() {
    const $body = $(document);

    if (T4SThemeSP.isTouch) {
      // 触摸设备使用点击
      $body.on('click', SELECTORS.swatchItem, function () {
        handleSwatchSelection($(this));
      });
    } else {
      // 桌面设备使用悬停意图
      $body.hoverIntent({
        selector: SELECTORS.swatchItem,
        sensitivity: 6,
        interval: 100,
        timeout: 100,
        over: function () {
          handleSwatchSelection($(this));
        },
        out: function () { },
      });
    }
  }

  // ==================== ResizeObserver ====================

  /**
   * 使用 ResizeObserver 监听产品项尺寸变化
   * 用于轮播图和 isotope 布局的自动调整
   */
  function initResizeObserver() {
    const selector =
      SELECTORS.resizeObserver +
      '.flickityt4s-enabled .t4s-product:not(.t4s_observered), ' +
      SELECTORS.resizeObserver +
      '.isotopet4s-enabled .t4s-product:not(.t4s_observered)';
    const $targets = $(selector);

    if ($targets.length === 0 || !window.ResizeObserver) {
      return;
    }

    const resizeObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        const $target = $(entry.target);
        const $container = $target.is(SELECTORS.resizeObserver) ? $target : $target.closest(SELECTORS.resizeObserver);

        let resizeTimeout;
        clearTimeout(resizeTimeout);

        $container.addClass('is-t4s--doing');

        resizeTimeout = setTimeout(function () {
          if ($container.hasClass('flickityt4s-enabled')) {
            $container.flickityt4s('resize');
          } else if ($container.hasClass('isotopet4s-enabled')) {
            $container.isotopet4s('layout');
          }
          $container.removeClass('is-t4s--doing');
        }, 28);
      });
    });

    $targets.each(function () {
      const $element = $(this);
      resizeObserver.observe(this);
      $element.addClass('t4s_observered');

      $element.one('destroy.observered', function () {
        resizeObserver.unobserve(this);
        $element.removeClass('t4s_observered');
      });
    });
  }

  // ==================== 评论系统 ====================

  /**
   * 重新加载评论星级显示
   */
  function reloadReviewBadges() {
    if (CONFIG.reviewApp === '1') {
      // Shopify Product Reviews (SPR)
      try {
        if (window.SPR && $('.spr-badge').length > 0) {
          SPR.initDomEls();
          SPR.loadBadges();
        }
      } catch (error) {
        console.warn('SPR review reload error:', error);
      }
    } else if (CONFIG.reviewApp === '8') {
      // Smart Reviews 或 Air Reviews
      if (typeof SMARTIFYAPPS !== 'undefined' && SMARTIFYAPPS.rv.installed) {
        SMARTIFYAPPS.rv.scmReviewsRate.actionCreateReviews();
      }
      if (typeof airReviewDisplayManager !== 'undefined' && airReviewDisplayManager.initialize) {
        airReviewDisplayManager.initialize();
      }
    } else if (CONFIG.reviewApp === '6') {
      // 其他评论系统
      $(document).trigger('reloadReview.t4s');
    }
  }

  /**
   * 动态加载评论系统脚本
   */
  function loadReviewScript() {
    if (CONFIG.reviewApp === '6' && T4Sconfigs.script12) {
      $script(T4Sconfigs.script12);
    }
  }

  // ==================== 工具函数 ====================

  /**
   * 安全的 JSON 解析
   * @param {string} jsonString - JSON 字符串
   * @returns {Object} 解析后的对象，失败返回空对象
   */
  function parseJSON(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('JSON parse error:', error, jsonString);
      return {};
    }
  }

  // ==================== 初始化设置 ====================

  // 如果启用色板限制，添加相应类名
  if (CONFIG.swatches.enableLimit) {
    $('body').addClass('t4s-pr-item-sw-limit');
    if (CONFIG.swatches.fixedNumber && CONFIG.swatches.fixedNumber > 0) {
      CONFIG.swatches.maxDefault = CONFIG.swatches.fixedNumber;
    }
  }

  // ==================== 公共 API ====================

  /**
   * 返回公共接口
   */
  return {
    // 主初始化函数
    init: initializeProducts,

    // 快速查看/快速购物
    initQuickVS: initQuickViewShop,
    openQuickVS: openQuickViewShop,
    // 色板相关
    recalculateSwatches: recalculateSwatchLimit,
    clickMoreSwatches: initSwatchMoreClick,
    swatchesClickHover: initSwatchClickHover,

    // ResizeObserver
    resizeObserver: initResizeObserver,

    // 评论系统
    reloadReview: reloadReviewBadges,
    loadjsRevew: loadReviewScript,

    // URL 管理
    updateColelction: updateCollectionUrls,
  };
}

function adminSelect() {
  const $document = $(document);
  const $body = $('body');

  $document.on(SHOPIFY_EVENTS.sectionSelect, function (event) {
    const sectionId = event.detail.sectionId;

    const $section = $(SECTION_IDS.prefix + sectionId);

    const finalSectionId = sectionId;
    const modalId = window.mainQVSectionId;

    if (finalSectionId === modalId) {
      // 处理快速查看/快速购买
      $body.trigger('modalt4s:closed');

      setTimeout(function () {
        const templateHtml = $section.find('>template').html();
        const openingClass = 't4s-opening-qv';

        const callback = function () {
          const $quickView = $('.t4s-product-quick-view');
          const $product = $quickView.find('[data-product-featured]:not(.initProducts__enabled)');
          $product.addClass('initProducts__enabled');

          new T4SThemeSP.Product($product);

          const $mainMedia = $quickView.find('[data-main-media]');
          if ($mainMedia.hasClass('flickityt4s') && !$mainMedia.hasClass('flickityt4s-enabled')) {
            $mainMedia[0].flickityt4s = new T4SThemeSP.Carousel($mainMedia[0]);
          }

          T4SThemeSP.PopupMFP();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        };

        T4SThemeSP.NTpopupInline(templateHtml, modalId, callback, openingClass);
        T4SThemeSP.PopupMFP();
        $body.trigger('modalt4s:opened');
      }, 500);
    }
  });
}

ltLazyLoad(function () {
  window.ltQv = LuteProductItem();
  // window.ltQv.initQuickVS();
  // setTimeout(() => {
  //   $('#test-btn').click();
  // }, 300);
  adminSelect();
});


async function addToCart(data) {
  const response = await fetch(`${window.T4Sroutes.cart_add_url}.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  return response
}

const ScenceCardVue = {
  template: '#card-temp',
  props: {
    id: String,
    discount: Number,
    code: String,
    localstorageKey: String,
    imgEl: String,
    cardEl: Object,
    productIds: {
      type: Array,
      default: () => ([])
    },
    items: {
      type: Array,
      default: () => ([])
    },
  },
  setup(props) {
    const { id, discount, code, items, productIds, localstorageKey, imgEl, cardEl } = props

    const imgRef = useTemplateRef("imgRef")

    onMounted(() => {
      $('body').on("lt:product:addItem", (event, data) => {
        const { bid, items: addItems, isEdit } = data
        if (id !== bid) return;
        if (isEdit) {

          const filterItems = itemsRef.value.filter(item => item !== window.ltQv.selected)
          const editItem = addItems.find(item => item.isEdit)
          const sameVariant = filterItems.find(v => v.vid === editItem.vid)
          if (sameVariant) {
            const otherItems = filterItems.filter(item => item !== sameVariant)
            editItem.quantity += sameVariant.quantity

            itemsRef.value = [...otherItems, editItem]
          } else {
            itemsRef.value = [...filterItems, ...addItems]
          }

          return
        }
        const newItems = []

        productIds.forEach(pid => {
          const addItem = addItems.find(item => item.id == pid)
          if (!addItem) {
            const pItems = itemsRef.value.filter(item => item.id == pid)
            newItems.push(...pItems)
            return
          }
          const otherItems = itemsRef.value.filter(item => item.id == pid && item.vid && item.vid !== addItem.vid)
          const sameVariant = itemsRef.value.find(v => v.vid === addItem.vid)
          if (sameVariant) addItem.quantity += sameVariant.quantity
          newItems.push(...otherItems, addItem)
        })

        itemsRef.value = newItems
      });

      imgRef.value.addEventListener("click", (e) => {
        if (e.target.classList.contains('tag')) {
          e.stopPropagation()
          const pid = e.target.getAttribute('data-pid')
          if (!pid) return;
          const prodItem = items.find(item => item.id == pid)
          handleAddVariant(prodItem)
        }
      })
    })

    const storageKey = `${localstorageKey}:${id}`
    const localItems = localStorage.getItem(storageKey)

    const itemsRef = ref(localItems ? JSON.parse(localItems) : items)
    const useDiscount = ref(true)


    watch(itemsRef, (newVal) => {
      localStorage.setItem(storageKey, JSON.stringify(newVal))
    }, { deep: true })

    function getPrice(price) {
      return T4SThemeSP.Currency?.formatMoney(price)
    }

    function getProductItems(pid) {
      return itemsRef.value.filter(item => item.id == pid)
    }

    const isAllDefault = computed(() => {
      return itemsRef.value.every(item => !item.vid || !item.is_selected)
    })



    const priceInfo = computed(() => {
      if (isAllDefault.value) {
        const price = items.reduce((acc, cur) => acc += cur.price, 0)
        const cpprice = items.reduce((acc, cur) => acc += cur.cpprice || cur.price, 0)
        const discountPrice = useDiscount.value ? items.reduce((acc, cur) => acc += Math.ceil(cur.price * discount / 100), 0) : price
        const discountOff = cpprice - discountPrice
        const hasVars = items.some(item => item.priceVaries)

        return {
          isAllDefault: isAllDefault.value,
          hasVars,
          price: discountPrice,
          cpprice,
          discountOff,
        }
      }

      const products = itemsRef.value.filter(i => i.vid && i.is_selected)
      const price = products.reduce((acc, cur) => acc += cur.price * cur.quantity, 0)
      const cpprice = products.reduce((acc, cur) => acc += (cur.cpprice || cur.price) * cur.quantity, 0)
      const discountPrice = useDiscount.value ? products.reduce((acc, cur) => acc += Math.ceil(cur.price * discount / 100) * cur.quantity, 0) : price
      const discountOff = cpprice - discountPrice
      const hasVars = products.some(item => item.priceVaries)

      return {
        hasVars,
        price: discountPrice,
        cpprice,
        discountOff,
      }
    })

    function handleSelect(item, e) {
      if (!item.vid && !item.is_selected) {
        e.preventDefault()
        e.stopPropagation()
        handleAddVariant(item)
        return
      }
    }

    function isLast(pid, vid) {
      const lastItem = itemsRef.value.findLast(i => i.id == pid)
      return lastItem.vid === vid
    }

    function getOther(item) {
      return items.find(i => i.id !== item.id)
    }

    async function handleAddVariant(item) {
      try {
        item.loading = true
        window.ltQv.selected = null
        window.ltQv.isEdit = false
        const otherProd = getOther(item)
        await window.ltQv.openQuickVS({
          productUrl: item.url,
          productId: item.id,
          sectionId: window.mainQVSectionId,
          bid: id,
          handle: otherProd.handle,
        })
      } finally {
        item.loading = false
      }
    }

    async function handleDefaultAdd() {
      window.ltQv.selected = null
      window.ltQv.isEdit = false
      const [p1, p2] = items
      await window.ltQv.openQuickVS({
        productUrl: p1.url,
        productId: p1.id,
        sectionId: window.mainQVSectionId,
        bid: id,
        handle: p2.handle
      })
    }

    async function handleEdit(item) {
      const otherProd = getOther(item)
      window.ltQv.selected = item
      window.ltQv.isEdit = true
      await window.ltQv.openQuickVS({
        productUrl: item.url,
        productId: item.id,
        sectionId: window.mainQVSectionId,
        bid: id,
        handle: otherProd.handle,
        variantId: item.vid
      })
    }

    function openToast() {
      const cardWrapper = cardEl.closest('.scene-card-wrapper')

      luteToast(cardWrapper)
    }

    const addLoading = ref(false)
    async function handleAdd() {
      try {
        addLoading.value = true
        if (isAllDefault.value) {
          await handleDefaultAdd()
          return
        }

        const items = itemsRef.value.filter(i => i.vid && i.is_selected).map(item => {
          return {
            id: item.vid,
            quantity: item.quantity || 1
          }
        })

        const discountCode = useDiscount.value ? code : null
        await fetch(window.Shopify.routes.root + "discount/" + discountCode)
        const res = await addToCart({ items })

        if (res.status === 200) {
          openToast()
          document.dispatchEvent(new CustomEvent('cart:refresh:opend'))
        }
      } finally {
        addLoading.value = false
      }
    }

    const buyLoading = ref(false)
    async function handleBuy() {
      try {
        buyLoading.value = true
        if (isAllDefault.value) {
          await handleDefaultAdd()
          return
        }

        const items = itemsRef.value.filter(i => i.vid && i.is_selected).map(item => {
          return {
            id: item.vid,
            quantity: item.quantity || 1
          }
        })

        const prodParams = items.reduce((acc, cur) => acc + `${cur.id}:${cur.quantity || 1},`, '')
        const checkUrl = `${window.T4Sroutes.cart_url}/${prodParams}?discount=${useDiscount.value ? code : ''}`
        const supportsViewTransition = typeof document.startViewTransition === 'function';
        if (supportsViewTransition) {
          document.startViewTransition(() => {
            window.location.href = checkUrl
          });
        } else {
          window.location.href = checkUrl
        }
      } finally {
        buyLoading.value = false
      }
    }

    return {
      id,
      discount,
      code,
      imgEl,
      imgRef,
      productIds,
      items: itemsRef,
      getPrice,
      getProductItems,
      priceInfo,
      useDiscount,
      handleSelect,
      handleAdd,
      handleBuy,
      isLast,
      handleAddVariant,
      handleEdit,
      addLoading,
      buyLoading,
    }
  }
}
class SceneCard extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const imgEl = this.querySelector('.card-img').innerHTML;
    const initData = JSON.parse(this.getAttribute('data') || '{}');
    const cardEl = this;
    this.mountApp({ ...initData, imgEl, cardEl });
  }
  mountApp(data) {
    const i18n = createI18n({
      locale: 'default',
      legacy: false,
      messages: {
        default: window.ltSceneI18n
      }
    })

    const app = createApp(ScenceCardVue, data)
    app.use(i18n)
    app.config.compilerOptions.delimiters = ['[[', ']]']
    app.config.compilerOptions.isCustomElement = (tag) => tag.startsWith('scene-')
    ltLazyLoad(() => {
      app.mount(this)
    })
  }
}
if (!customElements.get('scene-card')) {
  customElements.define('scene-card', SceneCard);
}


function luteToast(target) {
  let toastEl = target.querySelector(".deal25-toast")
  if (!toastEl) {
    const tempEl = document.querySelector("#toast-temp").content.cloneNode(true)
    target.appendChild(tempEl)
    toastEl = target.querySelector(".deal25-toast")
  }

  toastEl.classList.add("show");

  setTimeout(() => {
    toastEl.classList.remove("show");
    setTimeout(() => {
      target.removeChild(tempEl)
    }, 500);
  }, 3000);
}